import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, first, map, Subject, takeUntil, tap, zip } from 'rxjs';
import { LgvAPIService, LgvClientService, LgvConstants, LgvUserService, NotificationService } from '../../core/core.module';
import { RegexConstants, RegexValidator } from '../../core/utility/lgv-regex-validator';
import { LgvModel } from '../../data-models/longevity.model';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { environment as env } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';

@Component({
    selector: 'lus-edit-loan-scenario-modal',
    templateUrl: './edit-loan-scenario-modal.component.html',
    styleUrls: ['./edit-loan-scenario-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditLoanScenarioModalComponent implements OnInit {
    @Output() OnOKClicked: EventEmitter<string> = new EventEmitter();

    isLoading = false;
    isLoading$ = new BehaviorSubject(this.isLoading);
    scenarioForm: UntypedFormGroup;
    onDestroy$ = new Subject();

    clientInfo: LgvModel.ClientInfo;
    property: LgvModel.Property;
    loanScenario: LgvModel.LoanScenario;

    clientIdParam: string = null;
    propertyIdParam: string = null;

    get propertyAddress() {
        return this.lgvClientService.SelectedProperty$.value.propertyInfo.address + ' ' +
            this.lgvClientService.SelectedProperty$.value.propertyInfo.city + ' ' +
            this.lgvClientService.SelectedProperty$.value.propertyInfo.state + ' ' +
            this.lgvClientService.SelectedProperty$.value.propertyInfo.zip;
    }

    get readyForDTI(): boolean { return !!(this.scenarioForm.controls.totalDebtPayments.value) && !!(this.scenarioForm.controls.totalIncome.value) && this.scenarioForm.controls.totalDebtPayments.value > 0 && this.scenarioForm.controls.totalIncome.value > 0 }
    get DTI(): string { return this.readyForDTI ? (this.scenarioForm.controls.totalDebtPayments.value / this.scenarioForm.controls.totalIncome.value).toFixed(2).toString() : null }

    get PurchasePriceCtrl(): any { return this.scenarioForm.controls['purchasePrice']; }
    get LoanAmountCtrl(): any { return this.scenarioForm.controls['loanAmount']; }
    get DownPaymentCtrl(): any { return this.scenarioForm.controls['downPayment']; }
    get DownPaymentPctCtrl(): any { return this.scenarioForm.controls['downPaymentPct']; }

    residenceTypes$ = this.http.get<LgvModel.ResidenceType[]>(`${env.LgvApiBaseUrl}/types/residence`).pipe(
        first(),
        map(list => list.sort((x, y) => x.displayOrder - y.displayOrder))
    );

    constructor(
        private fb: UntypedFormBuilder,
        public lgvClientService: LgvClientService,
        public notificationService: NotificationService,
        public lgvApiService: LgvAPIService,
        public lgvUserService: LgvUserService,
        private route: ActivatedRoute,
        private http: HttpClient,
        public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
        public MatDialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: any) {

        this.clientIdParam = data.clientId;
        this.propertyIdParam = data.propertyId;
        this.loanScenario = data.lScenario;
    }

    ngOnInit(): void {
        zip(
            this.lgvClientService.SnapShot$.pipe(first(f => f !== null)),
            this.lgvClientService.ClientProperties$.pipe(
                first(f => f !== null),
                map(x => x.find(p => p.propertyInfo.id === this.propertyIdParam)))
        )
            .pipe(first())
            .subscribe(([clientSS, property]) => {
                this.clientInfo = clientSS.clientInfo;
                this.property = property;
                this.initializeForm();
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    initializeForm() {
        this.scenarioForm = this.fb.group({
            id: [this.loanScenario.id],
            clientId: [this.clientInfo.id],
            propertyId: [this.property.propertyInfo.id, [Validators.required]],
            purchasePrice: [this.loanScenario.purchasePrice, [Validators.required, RegexValidator(RegexConstants.GREATER_THAN_ZERO)]],
            downPayment: [this.loanScenario.downPayment, [Validators.required]],
            dpPercent: [ this.calculatDownPayment(this.loanScenario.purchasePrice, this.loanScenario.downPayment), [Validators.required, RegexValidator(RegexConstants.BETWEEN_0_AND_100_WITH_DECIMAL)]],
            loanAmount: [this.loanScenario.loanAmount, [Validators.required]],
            purchaseType: [this.loanScenario.residenceType, [Validators.required]],
            isPayingPoints: [this.loanScenario.payDownPoints, [Validators.required]],
        });

        this.residenceTypes$.pipe(first()).subscribe(types => {
            if (types && types.length > 0) {
                const type = types?.find(type => type?.name === this.loanScenario.residenceType)
                this.scenarioForm.controls?.purchaseType.setValue(type?.id);
            }
        });
    }

    onCancel(): void {
        this.dialogRef.close({ action: false });
    }

    onConfirmSave() {
        if (this.scenarioForm.invalid) { return; }

        const dialogRef = this.MatDialog.open(ConfirmationDialogComponent, {
            width: '400px',
            data: { Confirmation: LgvConstants.ClientProfileWarning }
        });

        dialogRef.afterClosed().subscribe(x => {
            if (x.action == true) {

                //this.scenarioForm.value.ownerId = this.scenarioForm.get("ownerId").value.id;
                const scenarioModel = {
                    ...this.scenarioForm.getRawValue(),
                    payDownPoints: this.scenarioForm.value.isPayingPoints,
                    residenceTypeId: this.scenarioForm.value.purchaseType
                };


                this.isLoading$.next(true);
                this.lgvClientService.SaveLoanScenario(scenarioModel).pipe(first()).subscribe(response => {
                    if (response) {
                        this.notificationService.success('Loan Scenario has been saved');
                        this.isLoading$.next(false);
                        this.dialogRef.close({ action: true });
                    }
                }, (err) => {
                    this.isLoading$.next(false);
                    this.notificationService.error('There was an error updating the loan scenario');
                    console.log(err);
                })

            }
        });

    }

    calculatDownPayment(purchasePrice, downPayment) {
        if (!purchasePrice || isNaN(downPayment)) return 20;
        return (downPayment / purchasePrice) * 100;
    }
}
